import { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import styled from 'styled-components';
import { spacing } from 'ui/theme';
import { BUTTON_SIZES, Button } from 'ui/button';
import { PageHeader } from 'ui/pageHeader';
import { TabsGroup, IndexTabsList, NavTabTrigger } from 'ui/tabsGroup';
import * as actions from '../actions';
import { MetricDefinitionsTable } from './MetricDefinitionsTable';
import {
  actionColumn,
  analyticsMetricDefinitionColumns,
  highLevelMetricDefinitionsColumns,
} from './MetricDefinitionsTableColumns';
import { selectAnalyticsMetricDefinitions, selectHighLevelMetricDefinitions } from '../selectors';
import { METRICS_TAB } from '../constants';
import { EditModal, UpdateMetricDefinition } from './EditModal';
import { DeleteModal } from './DeleteModal';
import { MetricDefinition } from '../types';
import { CSVImportModal } from './CSVImportModal';

const initialData = {
  id: '',
  metricName: '',
  definition: '',
  longDefinition: '',
  calculation: '',
  learnMoreLink: '',
  isMIReportingMetric: false,
  isCAReportingMetric: false,
};

const initialModalStates = {
  CREATE: {
    isOpen: false,
    type: '',
  },
  EDIT: {
    isOpen: false,
    type: '',
  },
  DELETE: {
    isOpen: false,
    type: '',
  },
  CSV: {
    isOpen: false,
    type: '',
  },
};

const CreateButton = styled(Button)`
  margin-right: ${spacing.base};
`;

const MetricDefinitions = () => {
  const dispatch = useDispatch();
  const analyticsMetricDefinitions = useSelector(selectAnalyticsMetricDefinitions);
  const highLevelMetricDefinitions = useSelector(selectHighLevelMetricDefinitions);
  const [modalStates, setModalStates] = useState(initialModalStates);
  const [selectedTab, setSelectedTab] = useState(METRICS_TAB.ANALYTICS);

  const [data, setData] = useState<UpdateMetricDefinition>({ ...initialData });

  useEffect(() => {
    dispatch(actions.fetchAnalyticsMetricDefinitions(''));
    dispatch(actions.fetchHighLevelMetricDefinitions(''));
  }, []);

  const handleOpenEditModal = (row: MetricDefinition) => {
    setData({
      ...row,
    });

    setModalStates({ ...initialModalStates, EDIT: { isOpen: true, type: selectedTab } });
  };

  const handleOpenCreateModal = () => {
    setData({
      ...initialData,
    });

    setModalStates({ ...initialModalStates, CREATE: { isOpen: true, type: selectedTab } });
  };

  const handleOpenDeleteModal = (row: MetricDefinition) => {
    setData({
      ...row,
    });
    setModalStates({ ...initialModalStates, DELETE: { isOpen: true, type: selectedTab } });
  };

  const closeEditModal = () => {
    setModalStates({ ...initialModalStates, EDIT: { isOpen: false, type: '' } });
  };

  const closeCreateModal = () => {
    setModalStates({ ...initialModalStates, CREATE: { isOpen: false, type: '' } });
  };

  const closeDeleteModal = () => {
    setModalStates({ ...initialModalStates, DELETE: { isOpen: false, type: '' } });
  };

  const openCSVModal = () => {
    setModalStates({ ...initialModalStates, CSV: { isOpen: true, type: selectedTab } });
  };

  const closeCSVModal = () => {
    setModalStates({ ...initialModalStates, CSV: { isOpen: false, type: '' } });
  };

  const handleCSVImport = (csvData: string) => {
    // Process the imported CSV data here
    if (modalStates.CSV.type === METRICS_TAB.ANALYTICS) {
      dispatch(
        actions.bulkUpdateAnalyticsMetricDefinition({
          postData: JSON.stringify({ csvContent: csvData }),
        }),
      );
    } else {
      dispatch(
        actions.bulkUpdateHighLevelMetricDefinition({
          postData: JSON.stringify({ csvContent: csvData }),
        }),
      );
    }
    closeCSVModal();
  };

  const handleUpdateSubmit = (updatedData: UpdateMetricDefinition) => {
    if (modalStates.EDIT.type === METRICS_TAB.ANALYTICS) {
      dispatch(
        actions.updateAnalyticsMetricDefinition({ id: updatedData.id, postData: updatedData }),
      );
    } else {
      dispatch(
        actions.updateHighLevelMetricDefinition({ id: updatedData.id, postData: updatedData }),
      );
    }
    setData({ ...initialData });
  };

  const handleCreateSubmit = (newData: UpdateMetricDefinition) => {
    if (modalStates.CREATE.type === METRICS_TAB.ANALYTICS) {
      dispatch(actions.createAnalyticsMetricDefinition({ postData: newData }));
    } else {
      dispatch(actions.createHighLevelMetricDefinition({ postData: newData }));
    }
    setData({ ...initialData });
  };

  const handleDeleteSubmit = () => {
    if (modalStates.DELETE.type === METRICS_TAB.ANALYTICS) {
      dispatch(actions.deleteAnalyticsMetricDefinition({ id: data.id }));
    } else {
      dispatch(actions.deleteHighLevelMetricDefinition({ id: data.id }));
    }
    setData({ ...initialData });
  };

  return (
    <>
      <PageHeader
        data-test="admin-metric-definitions-header"
        pageActions={
          selectedTab === METRICS_TAB.ANALYTICS ? (
            <>
              <CreateButton
                data-test="am-create-btn"
                onClick={handleOpenCreateModal}
                size={BUTTON_SIZES.LARGE}
              >
                Create new Analytics Metric Definition
              </CreateButton>
              <Button data-test="am-create-btn" onClick={openCSVModal} size={BUTTON_SIZES.LARGE}>
                Import Analytics Metric Definitions CSV
              </Button>
            </>
          ) : (
            <>
              <CreateButton
                data-test="hlm-csv-btn"
                onClick={handleOpenCreateModal}
                size={BUTTON_SIZES.LARGE}
              >
                Create new High Level Metric Definition
              </CreateButton>
              <Button data-test="hlm-csv-btn" onClick={openCSVModal} size={BUTTON_SIZES.LARGE}>
                Import High Level Metric Definitions CSV
              </Button>
            </>
          )
        }
      >
        Metric Definitions
      </PageHeader>
      <TabsGroup
        data-test="metric-definitions-tabs"
        value={selectedTab}
        onValueChange={(newTab) => setSelectedTab(newTab as METRICS_TAB)}
      >
        <IndexTabsList>
          <NavTabTrigger
            data-test={`${METRICS_TAB.ANALYTICS}-tab-header`}
            key={METRICS_TAB.ANALYTICS}
            value={METRICS_TAB.ANALYTICS}
          >
            Analytics metric definitions
          </NavTabTrigger>
          <NavTabTrigger
            data-test={`${METRICS_TAB.HIGH_LEVEL}-tab-header`}
            key={METRICS_TAB.HIGH_LEVEL}
            value={METRICS_TAB.HIGH_LEVEL}
          >
            High level metric definitions
          </NavTabTrigger>
        </IndexTabsList>
      </TabsGroup>
      {selectedTab === METRICS_TAB.ANALYTICS && (
        <>
          <MetricDefinitionsTable
            columns={[
              ...analyticsMetricDefinitionColumns,
              actionColumn({
                handleEditClick: handleOpenEditModal,
                handleDeleteClick: handleOpenDeleteModal,
              }),
            ]}
            rows={analyticsMetricDefinitions}
          />
        </>
      )}
      {selectedTab === METRICS_TAB.HIGH_LEVEL && (
        <>
          <MetricDefinitionsTable
            columns={[
              ...highLevelMetricDefinitionsColumns,
              actionColumn({
                handleEditClick: handleOpenEditModal,
                handleDeleteClick: handleOpenDeleteModal,
              }),
            ]}
            rows={highLevelMetricDefinitions}
          />
        </>
      )}
      {modalStates.DELETE.isOpen && (
        <DeleteModal
          isOpen={modalStates.DELETE.isOpen}
          onClose={closeDeleteModal}
          onDelete={handleDeleteSubmit}
          name={data.metricName}
        />
      )}
      {modalStates.CSV.isOpen && (
        <CSVImportModal
          isOpen={modalStates.CSV.isOpen}
          onClose={closeCSVModal}
          onImport={handleCSVImport}
        />
      )}
      {modalStates.EDIT.isOpen && (
        <EditModal
          isOpen={modalStates.EDIT.isOpen}
          onClose={closeEditModal}
          onSubmit={handleUpdateSubmit}
          initialValues={data}
          selectedTab={selectedTab}
          isCreateModal={false}
        />
      )}
      {modalStates.CREATE.isOpen && (
        <EditModal
          isOpen={modalStates.CREATE.isOpen}
          onClose={closeCreateModal}
          onSubmit={handleCreateSubmit}
          initialValues={data}
          selectedTab={selectedTab}
          isCreateModal={true}
        />
      )}
    </>
  );
};

export default MetricDefinitions;
